/** Central game configuration — tune values here instead of scattering magic numbers. */
export const CONFIG = {
  canvas: {
    selector: '#game-canvas',
  },
  hud: {
    rootSelector: '#hud-root',
  },
  renderer: {
    antialias: true,
    alpha: false,
    pixelRatioMax: 1.25,
    toneMappingExposure: 1.0,
  },
  camera: {
    fov: 75,
    near: 0.1,
    far: 650,
  },
  world: {
    backgroundColor: 0x000000,
    fog: {
      enabled: true,
      color: 0x000805,
      near: 30,
      far: 220,
    },
  },
  city: {
    seed: 12345,
    blockCount: 12,
    lotSize: 16,
    roadWidth: 8,
    sidewalkWidth: 2,
    chunkSize: 4,
  },
  loop: {
    maxDeltaSeconds: 0.1,
  },
  matrix: {
    primaryGreen: 0x00ff41,
    dimGreen: 0x003b00,
    darkGreen: 0x001408,
    primaryRed: 0xff2040,
    dimRed: 0x5a0018,
    darkRed: 0x140002,
  },
  /** Extra emissive for red accent surfaces (rain uses `rain.glowStrength`). */
  luminance: {
    accentGlowStrength: 2.35,
    npcGlowStrength: 2.15,
    shipGlowStrength: 2.28,
    vehicleGlowStrength: 2.32,
    /** Street lamp pillar (dimmer than head). */
    poleGlowStrength: 0.72,
  },
  player: {
    spawn: { x: 0, y: 0, z: 0 },
    eyeHeight: 1.65,
    radius: 0.35,
    bodyHeight: 1.7,
    walkSpeed: 5,
    sprintSpeed: 9,
    jumpForce: 6,
    gravity: -18,
    groundAcceleration: 52,
    groundDeceleration: 42,
    airAcceleration: 12,
    groundSnapDistance: 0.14,
    pitchSmoothing: 14,
    mouseSensitivity: 0.0022,
    pitchMin: -Math.PI / 2 + 0.05,
    pitchMax: Math.PI / 2 - 0.05,
    headBob: {
      enabled: true,
      verticalAmplitude: 0.032,
      lateralAmplitude: 0.011,
      frequency: 1.65,
      minSpeed: 0.5,
    },
    footstep: {
      minSpeed: 0.6,
    },
  },
  npc: {
    /** Test crowd sizes: 100, 250, 500 */
    count: 100,
    seed: 90210,
    speedMin: 1.0,
    speedMax: 2.4,
    navNodeSpacing: 5,
    avoidanceRadius: 1.1,
    playerAvoidRadius: 1.55,
    waitTimeMin: 1.0,
    waitTimeMax: 5.0,
    intersectionWaitMin: 0.35,
    intersectionWaitMax: 1.25,
    stuckTimeout: 3.5,
    /** Stagger expensive repaths / look-around (higher = cheaper at 500 NPCs). */
    thinkStride: 3,
    destinationSamples: 10,
    groupChance: 0.12,
    maxGroupSize: 4,
    personality: {
      speedMultiplierMin: 0.82,
      speedMultiplierMax: 1.18,
      waitScaleMin: 0.75,
      waitScaleMax: 1.35,
      lookAroundChanceMin: 0.04,
      lookAroundChanceMax: 0.22,
    },
    population: {
      /** Fraction of lot blocks in each tier (deterministic hash). */
      lowCutoff: 0.32,
      mediumCutoff: 0.72,
      tierWeights: {
        low: 0.2,
        medium: 1.0,
        high: 2.4,
      },
    },
  },
  rain: {
    enabled: true,
    seed: 0x00c0de,
    /** Total vertical streams; instance count = streamCount × maxStreamLength */
    streamCount: 420,
    maxStreamLength: 26,
    spreadMargin: 140,
    rainTop: 98,
    fallRange: 88,
    glyphSpacing: 0.36,
    glyphWidth: 0.34,
    speedMin: 9,
    speedMax: 24,
    lengthMin: 10,
    lengthMax: 26,
    brightnessMin: 0.72,
    brightnessMax: 1.45,
    windStrength: 0.42,
    windSpeed: 0.55,
    flipSpeed: 11,
    glowStrength: 2.05,
    lodNearDistance: 42,
    lodFarDistance: 155,
    lodNearGlyphs: 26,
    lodFarGlyphs: 7,
    lodNearGlyphScale: 1.0,
    lodFarGlyphScale: 1.45,
    recycleRadius: 200,
    recycleHalfExtent: 185,
    recycleInterval: 0.75,
  },
  graphics: {
    bloom: true,
    fog: true,
    scanlines: true,
    glitch: true,
    noise: true,
    chromaticAberration: true,
    rainDensity: 1.0,
    renderDistance: 120,
    fogColor: 0x001a0a,
    fogNear: 28,
    fogExp2: true,
    fogDensity: 0.011,
    ambientIntensity: 0.3,
    bloomStrength: 0.22,
    bloomRadius: 0.28,
    bloomThreshold: 0.85,
    vignetteStrength: 0.42,
    glitchMinInterval: 10,
    glitchMaxInterval: 30,
    glitchSeed: 0xbadcafe,
    /** When true, city/NPC glyphs freeze (saves uniform updates); rain still animates. */
    staticBinaryGlyphs: false,
    /** Fewer props on building facades (less geometry). */
    minimalBuildingProps: true,
    /** Canvas sign phrase/flicker updates (CPU). Set false for max FPS. */
    signFlicker: true,
  },
  streaming: {
    /** World meters per chunk (XZ). Chunk (0,0) is centered on spawn. */
    chunkSize: 128,
    /** Load chunks within this radius (meters). */
    renderDistance: 168,
    /** Full NPC simulation within this radius (meters). */
    simulationDistance: 90,
    /** Extra chunk rings kept loaded beyond render radius before unload. */
    unloadPadding: 1,
    /** Spread chunk mesh generation across frames (avoids long hitches). */
    maxChunksPerFrame: 2,
    /** Player collision uses colliders from this many chunk rings (Chebyshev). */
    collisionChunkRadius: 1,
  },
  ui: {
    menuRootSelector: '#menu-root',
    defaultQuality: 'medium',
    defaultPostProcessing: true,
    defaultNpcDensity: 100,
    /** Max pooled NPCs (settings can request up to this count). */
    npcPoolMax: 500,
  },
  debug: {
    /** Extra console.info from city/NPC/rain init (default off). */
    verboseLogging: false,
    /** PERF overlay (FPS, draw calls, LOD counts). */
    perfHud: false,
    /** Attach `globalThis.__matrixGame` in production when true. */
    exposeDevGlobal: false,
  },
  events: {
    enabled: true,
    seed: 0x14e001,
    glitchIntervalMin: 55,
    glitchIntervalMax: 130,
    glitchRetrySeconds: 8,
    glitchChancePerCheck: 0.55,
    glitchDurationMin: 1.1,
    glitchDurationMax: 2.0,
    glitchMaterialBoost: 1,
    glitchRainMultiplier: 1.85,
    glitchScreenStrength: 0.82,
  },
  flyingShips: {
    enabled: true,
    count: 20,
    seed: 160016,
    altitudeMin: 28,
    altitudeMax: 46,
    altitudeWobble: 1.6,
    orbitRadiusMin: 58,
    orbitRadiusMax: 118,
    speedMin: 0.07,
    speedMax: 0.16,
    bankAmplitude: 0.07,
    recenterDistance: 56,
  },
  interiors: {
    enabled: true,
    interactKey: 'KeyE',
    enterRadius: 2.35,
    exitRadius: 1.85,
    fadeSeconds: 0.32,
    exteriorSpawnDistance: 1.55,
    cellBaseY: -120,
    cellSpacing: 32,
    designCount: 10,
  },
  roadVehicles: {
    enabled: true,
    count: 10,
    seed: 170017,
    speedMin: 7,
    speedMax: 15,
    travelSpan: 88,
    rideHeight: 0.42,
    recenterDistance: 52,
  },
  atmosphere: {
    particleCount: 90,
    particleSpread: 42,
    particleHeight: 14,
    particleSize: 0.12,
    skylineCount: 10,
    skylineRadius: 195,
    skylineRecenterDistance: 48,
    heightFogStart: 5,
    heightFogFarBoost: 140,
  },
  audio: {
    enabled: true,
    seed: 0xa0010,
    masterVolume: 0.85,
    ambientVolume: 0.55,
    musicVolume: 0.48,
    sfxVolume: 0.7,
    /** Set paths when you add files under public/audio/ — null = procedural only. */
    assets: {
      footstepWalk: null,
      footstepSprint: null,
      uiClick: null,
      glitch: null,
      ambientHum: null,
      /** Optional loop — e.g. `/audio/matrix-theme.ogg` */
      backgroundMusic: null,
    },
    music: {
      enabled: true,
      masterGain: 1,
      /** D2 — dark Matrix undertone */
      rootFrequency: 73.42,
      padFrequencies: [73.42, 92.5, 110, 146.83],
      padGain: 0.055,
      bassGain: 0.072,
      bassPulseHz: 0.4,
      arpGain: 0.042,
      arpStepMin: 0.2,
      arpStepMax: 0.36,
      arpChance: 0.9,
      arpPattern: [0, 2, 4, 2, 1, 3, 0, 4, 2, 1, 3, 2],
      arpScale: [1, 1.2, 1.335, 1.5, 1.78],
      filterHz: 880,
      filterLfoHz: 0.035,
      filterLfoDepth: 220,
      menuMix: 0.6,
      simulationMix: 1,
      assetGain: 0.58,
    },
    ambient: {
      humFrequencies: [58, 116, 174],
      humGain: 0.045,
      cityGain: 0.08,
      cityFilterHz: 420,
      electronicGain: 0.018,
      electronicIntervalMin: 2.5,
      electronicIntervalMax: 7.5,
    },
    footsteps: {
      seed: 44001,
      walkGain: 0.14,
      sprintGain: 0.2,
    },
    npcFootsteps: {
      minSpeed: 0.28,
      /** Walk cadence multiplier — higher = more steps per second (player default 1.75). */
      cadence: 2.9,
      maxDistance: 32,
      maxPerFrame: 18,
      nearPriorityDistance: 14,
      nearMaxPerFrame: 28,
      gainScale: 0.95,
      /** Extra multiplier on road / sidewalk beside street. */
      roadGainScale: 1.55,
      panStrength: 0.85,
    },
    uiSounds: {
      seed: 88002,
      clickFrequency: 880,
      clickGain: 0.06,
      glitchGain: 0.09,
    },
    crowd: {
      hearRadius: 24,
      fullDensityCount: 14,
      bedGain: 0.2,
      filterHz: 520,
      filterQ: 0.55,
      filterSweep: 280,
      chirpGain: 0.052,
      chirpChance: 0.72,
      chirpIntervalMin: 0.35,
      chirpIntervalMax: 1.4,
    },
    ships: {
      maxHearDistance: 135,
      engineGain: 0.26,
      engineFrequencies: [58, 116, 232],
      filterHz: 420,
      filterSweep: 380,
      panStrength: 0.85,
    },
    vehicles: {
      enabled: true,
      maxHearDistance: 46,
      engineGain: 0.11,
      engineFrequencies: [88, 176, 264],
      filterHz: 520,
      filterSweep: 420,
      panStrength: 0.9,
      speedReference: 14,
    },
    rain: {
      enabled: true,
      bedGain: 0.025,
      intensityScale: 0.50,
      filterHz: 2100,
      filterQ: 0.55,
      highPassHz: 380,
      filterLfoHz: 0.12,
      filterLfoDepth: 520,
      dripGain: 0.01,
      dripChance: 0.28,
      dripIntervalMin: 0.08,
      dripIntervalMax: 0.28,
    },
  },
  performance: {
    /** Prefer `debug.perfHud`. Kept for backward compatibility. */
    debugHud: false,
    /** Full NPC AI + avoidance (meters). */
    npcFullDistance: 55,
    /** Reduced update rate, walk-only (meters). */
    npcMediumDistance: 95,
    /** Matches streaming simulationDistance if unset. */
    npcFarDistance: 0,
    /** Hide mesh + minimal tick (meters). */
    npcCullDistance: 185,
    npcMediumThinkStride: 2,
    /** Min seconds between A* runs per NPC. */
    pathfindCooldownSeconds: 0.45,
    /** Bloom pass resolution scale (0.5 = half res, cheaper). */
    bloomResolutionScale: 0.65,
    /** Scale rain streams under load (multiplies rainDensity). */
    rainDensityScale: 1,
  },
};
